// Loads the Google Tag Manager container from our own first-party endpoint
// (GTM_SOURCE = https://analytics.herbertseidl.at) instead of Google.
//
// Why hand-rolled instead of `@nuxt/scripts`: the pinned `@nuxt/scripts`
// hardcodes `www.googletagmanager.com` for the loader and has no option to
// point it at a server-side-GTM domain, and it bundles the loader at build
// time — which froze the container (published GTM changes never reached the
// site without a frontend redeploy) and broke Preview mode. Loading it live
// from our sGTM endpoint fixes both: publishes propagate, and the request is
// first-party.
//
// Loading is deferred until the first user interaction (with a 5s fallback) to
// keep the GTM bundle — which also injects Cookiebot — off the critical render
// path. Debug/Preview bypasses the defer, see below.

export default defineNuxtPlugin(() => {
  const { GTM_ID, GTM_SOURCE } = useRuntimeConfig().public as {
    GTM_ID?: string;
    GTM_SOURCE?: string;
  };
  if (!GTM_ID || !GTM_SOURCE) return;

  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];

  let loaded = false;
  const inject = (src: string) => {
    if (loaded) return;
    loaded = true;
    w.dataLayer!.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
  };

  // GTM Preview / debug mode: load the container immediately (skip the defer)
  // AND from the live Google endpoint — web-container Preview is served by
  // Google, not by our sGTM, so Tag Assistant can only connect to gtm.js
  // loaded from googletagmanager.com with the preview params it appended.
  const isPreview =
    /[?&#]gtm_(debug|preview|auth)=/.test(window.location.href) ||
    document.referrer.startsWith('https://tagassistant.google.com');

  if (isPreview) {
    const gtmParams = [...new URLSearchParams(window.location.search)]
      .filter(([key]) => key.startsWith('gtm_'))
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    inject(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}${gtmParams ? `&${gtmParams}` : ''}`);
    return;
  }

  // Normal load: first-party, deferred.
  const firstPartySrc = `${GTM_SOURCE}/gtm.js?id=${GTM_ID}`;
  const interactionEvents = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

  const onInteract = () => {
    teardown();
    inject(firstPartySrc);
  };
  const teardown = () => interactionEvents.forEach(evt => window.removeEventListener(evt, onInteract));

  interactionEvents.forEach(evt => window.addEventListener(evt, onInteract, { once: true, passive: true }));

  const timer = setTimeout(() => {
    teardown();
    inject(firstPartySrc);
  }, 5000);

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      clearTimeout(timer);
      teardown();
    });
  }
});
